import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { compatAPI } from '../../utils/api';

const PROTOCOLS = ['dlna', 'chromecast', 'miracast', 'zcd'];
const VERDICT_OPTIONS = ['verified', 'works', 'partial', 'broken', 'untested', 'not_applicable'];
const PIPELINE_OPTIONS = ['direct_play', 'remux', 'transcode_audio', 'transcode_video', 'transcode_full'];

export default function CompatDeviceEditPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [device, setDevice] = useState(null);
    const [verdicts, setVerdicts] = useState({ protocol_verdicts: [], format_verdicts: [], feature_verdicts: [] });
    const [activeTab, setActiveTab] = useState('identity');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        loadDevice();
    }, [id]);

    const loadDevice = async () => {
        try {
            const [devRes, verdRes] = await Promise.all([
                compatAPI.getDevices(),
                compatAPI.getVerdicts(id),
            ]);
            const d = devRes.data.devices?.find(d => String(d.device_id) === String(id));
            setDevice(d);
            setVerdicts(verdRes.data);
        } catch (err) {
            console.error('Load error:', err);
        } finally {
            setLoading(false);
        }
    };

    const saveDevice = async () => {
        setSaving(true);
        try {
            await compatAPI.updateDevice(id, device);
            setMessage('Saved!');
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            setMessage('Save failed: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const saveVerdict = async (verdictId, table, updates) => {
        try {
            await compatAPI.updateVerdict(verdictId, { table, ...updates });
            await loadDevice();
            setMessage('Verdict saved!');
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            setMessage('Save failed: ' + err.message);
        }
    };

    const addProtocolVerdict = async (protocol) => {
        try {
            await compatAPI.createVerdict({
                table: 'protocol',
                device_id: parseInt(id),
                protocol,
                verdict: 'untested',
                notes: '',
            });
            await loadDevice();
        } catch (err) {
            setMessage('Add failed: ' + err.message);
        }
    };

    if (loading) return <div className="text-gray-400">Loading...</div>;
    if (!device) return <div className="text-red-500">Device not found</div>;

    const tabs = [
        { key: 'identity', label: 'Identity' },
        { key: 'protocols', label: 'Protocol Verdicts' },
        { key: 'formats', label: 'Format Matrix' },
        { key: 'notes', label: 'Test Notes' },
    ];

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <button onClick={() => navigate('/admin/compat/devices')}
                        className="text-sm text-zebcast-purple hover:underline mb-2">&larr; Back to devices</button>
                    <h2 className="text-2xl font-bold text-gray-900">{device.display_name}</h2>
                </div>
                {message && <span className="text-sm text-green-600 bg-green-50 px-3 py-1 rounded">{message}</span>}
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-6">
                    {tabs.map(tab => (
                        <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                                activeTab === tab.key
                                    ? 'border-zebcast-purple text-zebcast-purple'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}>{tab.label}</button>
                    ))}
                </nav>
            </div>

            {/* Identity Tab */}
            {activeTab === 'identity' && (
                <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                            <input className="w-full border rounded-md px-3 py-2 text-sm" value={device.slug || ''}
                                onChange={e => setDevice({...device, slug: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                            <input className="w-full border rounded-md px-3 py-2 text-sm" value={device.display_name || ''}
                                onChange={e => setDevice({...device, display_name: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                            <input className="w-full border rounded-md px-3 py-2 text-sm" value={device.brand || ''}
                                onChange={e => setDevice({...device, brand: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                            <input className="w-full border rounded-md px-3 py-2 text-sm" value={device.category || ''}
                                onChange={e => setDevice({...device, category: e.target.value})} />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">OUI Prefixes (comma-separated)</label>
                        <input className="w-full border rounded-md px-3 py-2 text-sm"
                            value={(device.oui_prefixes || []).join(', ')}
                            onChange={e => setDevice({...device, oui_prefixes: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Resolved IDs (comma-separated)</label>
                        <input className="w-full border rounded-md px-3 py-2 text-sm"
                            value={(device.resolved_ids || []).join(', ')}
                            onChange={e => setDevice({...device, resolved_ids: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Friendly Name Patterns (comma-separated, use % for wildcards)</label>
                        <input className="w-full border rounded-md px-3 py-2 text-sm"
                            value={(device.friendly_name_patterns || []).join(', ')}
                            onChange={e => setDevice({...device, friendly_name_patterns: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} />
                    </div>
                    <button onClick={saveDevice} disabled={saving}
                        className="px-4 py-2 bg-zebcast-purple text-white rounded-md hover:bg-zebcast-dark transition-colors disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            )}

            {/* Protocol Verdicts Tab */}
            {activeTab === 'protocols' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="space-y-4">
                        {verdicts.protocol_verdicts.map(pv => (
                            <div key={pv.verdict_id} className="border rounded-md p-4">
                                <div className="flex items-center gap-4 mb-2">
                                    <span className="font-bold text-sm uppercase">{pv.protocol}</span>
                                    <select value={pv.verdict}
                                        onChange={e => saveVerdict(pv.verdict_id, 'protocol', { verdict: e.target.value })}
                                        className="border rounded px-2 py-1 text-sm">
                                        {VERDICT_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                    <span className="text-xs text-gray-400">
                                        {pv.telemetry_sample_size || 0} sessions, {Math.round((pv.telemetry_success_rate || 0) * 100)}% success
                                    </span>
                                    <label className="text-xs">
                                        <input type="checkbox" checked={pv.manual_override}
                                            onChange={e => saveVerdict(pv.verdict_id, 'protocol', { manual_override: e.target.checked })}
                                            className="mr-1" />
                                        Manual override
                                    </label>
                                </div>
                                <input placeholder="Public notes..." className="w-full border rounded px-2 py-1 text-sm"
                                    defaultValue={pv.notes_public || ''} onBlur={e => saveVerdict(pv.verdict_id, 'protocol', { notes: e.target.value })} />
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 flex gap-2">
                        {PROTOCOLS.filter(p => !verdicts.protocol_verdicts.find(v => v.protocol === p)).map(p => (
                            <button key={p} onClick={() => addProtocolVerdict(p)}
                                className="px-3 py-1 text-sm border border-dashed rounded-md text-gray-500 hover:text-zebcast-purple hover:border-zebcast-purple">
                                + Add {p}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Format Matrix Tab */}
            {activeTab === 'formats' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                    {verdicts.format_verdicts.length === 0 ? (
                        <p className="text-gray-400 text-sm">No format verdicts yet. They will be auto-populated from telemetry data.</p>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Protocol</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Video</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Audio</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Container</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Verdict</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pipeline</th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Samples</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {verdicts.format_verdicts.map(fv => (
                                    <tr key={fv.verdict_id}>
                                        <td className="px-3 py-2 text-sm">{fv.protocol}</td>
                                        <td className="px-3 py-2 text-sm font-mono">{fv.video_codec}</td>
                                        <td className="px-3 py-2 text-sm font-mono">{fv.audio_codec}</td>
                                        <td className="px-3 py-2 text-sm font-mono">{fv.container || '\u2014'}</td>
                                        <td className="px-3 py-2 text-sm">
                                            <select value={fv.verdict}
                                                onChange={e => saveVerdict(fv.verdict_id, 'format', { verdict: e.target.value })}
                                                className="border rounded px-1 py-0.5 text-xs">
                                                {VERDICT_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-3 py-2 text-sm">
                                            <select value={fv.pipeline || ''}
                                                onChange={e => saveVerdict(fv.verdict_id, 'format', { verdict: fv.verdict, pipeline: e.target.value })}
                                                className="border rounded px-1 py-0.5 text-xs">
                                                <option value="">{'\u2014'}</option>
                                                {PIPELINE_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-3 py-2 text-sm text-gray-400">{fv.telemetry_sample_size || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Test Notes Tab */}
            {activeTab === 'notes' && (
                <div className="bg-white rounded-lg shadow-md p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Internal Test Notes</label>
                    <textarea className="w-full border rounded-md px-3 py-2 text-sm h-64 font-mono"
                        value={device.test_notes || ''}
                        onChange={e => setDevice({...device, test_notes: e.target.value})} />
                    <button onClick={saveDevice} disabled={saving}
                        className="mt-4 px-4 py-2 bg-zebcast-purple text-white rounded-md hover:bg-zebcast-dark transition-colors disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save Notes'}
                    </button>
                </div>
            )}
        </div>
    );
}
